import { Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { RecaptchaVerifier, signInWithPhoneNumber, type ConfirmationResult } from "firebase/auth";
import { firebase } from "@core/firebase/app";
import { useAppState } from "@core/state/AppState";
import { track } from "@core/services/analytics";

const formatPhone = (value: string): string => {
  const digits = value.replace(/[^0-9+]/g, "");
  if (!digits.startsWith("+")) {
    return `+${digits}`;
  }
  return digits;
};

const formatPhoneDisplay = (value: string): string => {
  // Remove all non-digit characters except +
  const cleaned = value.replace(/[^0-9+]/g, "");

  // If it starts with +1, format as US phone number
  if (cleaned.startsWith("+1")) {
    const digits = cleaned.substring(2); // Remove +1
    if (digits.length === 0) return "+1 ";
    if (digits.length <= 3) return `+1 (${digits}`;
    if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  // For other country codes, just return with + prefix
  return cleaned.startsWith("+") ? cleaned : `+${cleaned}`;
};

export function LoginPage() {
  const appState = useAppState();
  const state = appState.snapshot();
  const navigate = useNavigate();

  const [phoneNumber, setPhoneNumber] = createSignal("+1 ");
  const [verificationCode, setVerificationCode] = createSignal("");
  const [confirmation, setConfirmation] = createSignal<ConfirmationResult | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [isSending, setIsSending] = createSignal(false);
  const [isConfirming, setIsConfirming] = createSignal(false);

  let recaptchaVerifier: RecaptchaVerifier | null = null;

  onMount(() => {
    // Track page view
    track('page_view', { page_location: location.href, page_title: document.title });

    recaptchaVerifier = new RecaptchaVerifier(firebase.auth, "client-login-recaptcha", {
      size: "invisible",
      callback: () => {
        /* automatically handled */
      }
    });
    recaptchaVerifier?.render().catch((err) => {
      console.warn("reCAPTCHA render failed", err);
    });
  });

  onCleanup(() => {
    recaptchaVerifier?.clear?.();
    recaptchaVerifier = null;
  });

  createEffect(() => {
    if (state.authStatus() === "signed-in") {
      navigate("/", { replace: true });
    }
  });

  const handleSendCode = async (event: Event) => {
    event.preventDefault();
    if (isSending()) {
      return;
    }
    const value = formatPhone(phoneNumber());
    if (!/^\+[1-9][0-9]{7,14}$/.test(value)) {
      setError("Enter a valid phone number including country code.");
      return;
    }

    setError(null);
    setIsSending(true);

    try {
      if (!recaptchaVerifier) {
        recaptchaVerifier = new RecaptchaVerifier(firebase.auth, "client-login-recaptcha", { size: "invisible" });
        await recaptchaVerifier.render();
      }
      const result = await signInWithPhoneNumber(firebase.auth, value, recaptchaVerifier);
      setConfirmation(result);
      appState.setAuthStatus("verifying");
    } catch (err) {
      console.error("Failed to send verification code", err);
      setError("We couldn't send a code. Please try again.");
      recaptchaVerifier?.clear?.();
      recaptchaVerifier = null;
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async (event: Event) => {
    event.preventDefault();
    if (!confirmation() || isConfirming()) {
      return;
    }
    const code = verificationCode().trim();
    if (code.length < 6) {
      setError("Enter the 6-digit code we sent you.");
      return;
    }

    setError(null);
    setIsConfirming(true);

    try {
      await confirmation()!.confirm(code);
    } catch (err) {
      console.error("Code confirmation failed", err);
      setError("That code didn't match. Please try again.");
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div class="login-screen">
      {/* <div class="login-backdrop"></div> */}
      <div class="login-card">
        <div class="login-logo">
          <img src="/logo.png" alt="HappyTidy" />
        </div>
        <h1 class="login-title">HappyTidy Client</h1>
        <p class="login-subtitle">Sign in with your verified phone number to manage cleanings and chats.</p>

        <Show when={!confirmation()}>
          <form class="login-form" onSubmit={handleSendCode}>
            <label class="input-label" for="client-phone">
              <span>Phone number</span>
              <input
                id="client-phone"
                name="phone"
                type="tel"
                class="input-control"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber()}
                onInput={(event) => {
                  const formatted = formatPhoneDisplay(event.currentTarget.value);
                  setPhoneNumber(formatted);
                  event.currentTarget.value = formatted;
                }}
                autocomplete="tel"
                required
              />
            </label>
            <p class="input-note">Protected by reCAPTCHA.</p>
            <button type="submit" class="login-button" disabled={isSending()}>
              <span>{isSending() ? "Sending code…" : "Send verification code"}</span>
            </button>
          </form>
        </Show>

        <Show when={confirmation()}>
          <form class="login-form" onSubmit={handleVerifyCode}>
            <label class="input-label" for="client-code">
              Enter the 6-digit code we sent to <strong>{phoneNumber()}</strong>
            </label>
            <input
              id="client-code"
              name="code"
              type="text"
              inputmode="numeric"
              class="input-control"
              placeholder="123456"
              maxlength="6"
              value={verificationCode()}
              onInput={(event) => setVerificationCode(event.currentTarget.value)}
              required
            />
            <button type="submit" class="login-button" disabled={isConfirming()}>
              <span>{isConfirming() ? "Verifying…" : "Confirm and sign in"}</span>
            </button>
            <button
              type="button"
              class="login-link"
              onClick={() => {
                setConfirmation(null);
                setVerificationCode("");
              }}
            >
              Use a different number
            </button>
          </form>
        </Show>

        <Show when={error()}>
          <div class="login-error">
            <i class="fa-solid fa-circle-exclamation"></i>
            <span>{error()}</span>
          </div>
        </Show>

        <div class="login-footer">
          <p>Secure access for HappyTidy clients.</p>
          <p class="login-version">v{__APP_VERSION__} • {__BUILD_TIME__}</p>
        </div>
        <div id="client-login-recaptcha" class="recaptcha-container" />
      </div>
    </div>
  );
}

